import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { SeasonProvider } from "@/context/SeasonContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Pages
import Index from "./pages/Index";
import Admin from "./pages/Admin";
import AdminLogs from "./pages/AdminLogs";
import AdminActivityLog from "./pages/AdminActivityLog";
import AdminOperations from "./pages/AdminOperations";
import AdminEmails from "./pages/AdminEmails";
import NotFound from "./pages/NotFound";

// Admin panel
import { AdminLayout } from "@/components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminLeagues from "./pages/admin/AdminLeagues";
import AdminPlatformSettings from "./pages/admin/AdminPlatformSettings";
import AdminLeagueDetail from "./pages/admin/AdminLeagueDetail";
import Landing from "./pages/Landing";
import LeagueSettings from "./pages/LeagueSettings";
import LeagueMembers from "./pages/LeagueMembers";
import LeagueEmails from "./pages/LeagueEmails";
import LeagueBilling from "./pages/LeagueBilling";
import Billing from "./pages/Billing";
import Profile from "./pages/Profile";
import Login from "./pages/auth/Login";
import Logout from "./pages/auth/Logout";
import Register from "./pages/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import AcceptInvite from "./pages/auth/AcceptInvite";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import { CookieConsent } from "./components/CookieConsent";

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <SeasonProvider>
            <ErrorBoundary>
              <Routes>
              {/* Public landing page */}
              <Route path="/" element={<Landing />} />
              
              {/* Public auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register/success" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/invite/:code" element={<AcceptInvite />} />

              {/* Public pages */}
              <Route path="/about" element={<About />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />

              {/* Protected routes - top-level pages */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/picks"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/standings"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fixtures"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rules"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/history"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/members"
                element={
                  <ProtectedRoute>
                    <LeagueMembers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <LeagueSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/emails"
                element={
                  <ProtectedRoute>
                    <LeagueEmails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payouts"
                element={
                  <ProtectedRoute>
                    <LeagueBilling />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <Billing />
                  </ProtectedRoute>
                }
              />
              {/* Admin panel — ProtectedRoute ensures auth, AdminLayout checks isSuperAdmin */}
              <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/leagues" element={<AdminLeagues />} />
                <Route path="/admin/leagues/:leagueId" element={<AdminLeagueDetail />} />
                <Route path="/admin/leagues/:leagueId/:tab" element={<AdminLeagueDetail />} />
                <Route path="/admin/settings" element={<AdminPlatformSettings />} />
                <Route path="/admin/tools" element={<Admin />} />
                <Route path="/admin/operations" element={<AdminOperations />} />
                <Route path="/admin/logs" element={<AdminLogs />} />
                <Route path="/admin/activity" element={<AdminActivityLog />} />
                <Route path="/admin/emails" element={<AdminEmails />} />
                <Route path="/admin/emails/:slug" element={<AdminEmails />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              <CookieConsent />
            </ErrorBoundary>
            </SeasonProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
